import { useAtom } from 'jotai';
import Cookies from 'js-cookie';
import dynamic from 'next/dynamic';
import React, { useEffect, useState } from 'react';

import { SERVICEBOX_COOKIE, SEPA_MODAL_DISMISSED } from '../../../static/cookies';

// Atoms map
import { openCookieConcent } from '../../../state/application';
import { cartOpen } from '../../../state/cart';
import { navigationOpen } from '../../../state/navigation';
import { isCatalogueMenuOpenAtom } from '../../../state/selection';
import { vehiclePickerOpen } from '../../../state/vehicle';
import { isSearchOpen } from '../../../components/modules/search/state';

// Heavy imports defined with ssr: false (Only downloaded when React evaluates them)
const H_VehiclePicker = dynamic(() => import('../v2/vehicle-picker'), { ssr: false });
const H_CataloguePicker = dynamic(() => import('../catalogue-picker'), { ssr: false });
const H_Cart = dynamic(() => import('../cart'), { ssr: false });
const H_CookieConcent = dynamic(() => import('../cookie-concent'), { ssr: false });
const H_Menu = dynamic(() => import('../menu'), { ssr: false });
const H_Search = dynamic(() => import('../search'), { ssr: false });
const H_UsModal = dynamic(() => import('../us-modal'), { ssr: false });
const H_UkModal = dynamic(() => import('../uk-modal'), { ssr: false });
const H_GlobalUserModal = dynamic(() => import('../global-modal'), { ssr: false });
const H_VehiclePickerAdvanced = dynamic(() => import('../v2/vehicle-picker-advanced'), { ssr: false });
const H_SepaModal = dynamic(() => import('../sepa-modal'), { ssr: false });

const useHasOpened = (isOpen: boolean) => {
  const [hasOpened, setHasOpened] = useState(isOpen);
  useEffect(() => {
    if (isOpen && !hasOpened) setHasOpened(true);
  }, [isOpen, hasOpened]);
  return hasOpened;
};

const useHasInteracted = () => {
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    const handleInteraction = () => {
      setHasInteracted(true);
      events.forEach((e) => window.removeEventListener(e, handleInteraction));
    };

    const events = ['mousedown', 'mousemove', 'touchstart', 'scroll', 'keydown'];

    events.forEach((e) =>
      window.addEventListener(e, handleInteraction, { once: true, passive: true })
    );

    return () => {
      events.forEach((e) => window.removeEventListener(e, handleInteraction));
    };
  }, []);

  return hasInteracted;
};

export const VehiclePickerLazy = () => {
  const [open] = useAtom(vehiclePickerOpen);
  const hasOpened = useHasOpened(open);
  if (!hasOpened) return null;
  return <H_VehiclePickerAdvanced />;
};

export const CataloguePickerLazy = () => {
  const [open] = useAtom(isCatalogueMenuOpenAtom);
  const hasOpened = useHasOpened(open as boolean);
  if (!hasOpened) return null;
  return <H_CataloguePicker />;
};

export const CartLazy = () => {
  const [open] = useAtom(cartOpen);
  const hasOpened = useHasOpened(open);
  if (!hasOpened) return null;
  return <H_Cart />;
};

export const MenuLazy = () => {
  const [open] = useAtom(navigationOpen);
  const hasOpened = useHasOpened(open);
  if (!hasOpened) return null;
  return <H_Menu />;
};

export const SearchLazy = () => {
  const [open] = useAtom(isSearchOpen);
  const hasOpened = useHasOpened(open);
  if (!hasOpened) return null;
  return <H_Search />;
};

export const CookieConcentLazy = () => {
  const [open] = useAtom(openCookieConcent);
  const [mounted, setMounted] = useState(false);
  const [isConsented, setIsConsented] = useState(true);

  useEffect(() => {
    setMounted(true);
    // If not consented, force component to load
    setIsConsented(!!parseInt(Cookies.get('_cookie_required') || '0', 10));
  }, []);

  if (!mounted) return null;
  // If consented AND the modal wasn't explicitly triggered open, block chunk download
  if (isConsented && !open) return <React.Fragment />;

  return <H_CookieConcent />;
};

export const UsModalLazy = () => {
  const hasInteracted = useHasInteracted();
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (Cookies.get('isUSviewer') === 'true') setIsValid(true);
  }, []);

  if (!hasInteracted || !isValid) return null;
  return <H_UsModal />;
};

export const UkModalLazy = () => {
  const hasInteracted = useHasInteracted();
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    const isUK = Cookies.get('isUKviewer');
    if (isUK && isUK !== 'false') setIsValid(true);
  }, []);

  if (!hasInteracted || !isValid) return null;
  return <H_UkModal />;
};

export const GlobalUserModalLazy = () => {
  const hasInteracted = useHasInteracted();
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    const isGlobal = Cookies.get('isGlobalViewer');
    if (isGlobal && isGlobal !== 'false') setIsValid(true);
  }, []);

  if (!hasInteracted || !isValid) return null;
  return <H_GlobalUserModal />;
};

export const SepaModalLazy = () => {
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    const isDismissed = Cookies.get(SEPA_MODAL_DISMISSED);
    if (isDismissed === 'true') {
      return;
    }

    const sbCookie = Cookies.get(SERVICEBOX_COOKIE);
    if (sbCookie) {
      try {
        const parsed = JSON.parse(sbCookie);
        if (parsed && parsed.is_first_login === true) {
          setIsValid(true);
        }
      } catch (e) {
        if (sbCookie.includes('is_first_login') && !sbCookie.includes('is_first_login=false')) {
          setIsValid(true);
        }
      }
    }
  }, []);

  if (!isValid) return null;
  return <H_SepaModal />;
};
